/*
 * Brand test (chair caps DK), stage 2 design.
 *
 * 1. <bt-set-cards> (blocks/bt-set-cards.liquid): the set cards set the product form's quantity
 *    (2 sæt = quantity 2 of the chosen variant), show the chosen card's total and price per chair
 *    in the price under the title and in the sticky bar ([data-bt-amount] / [data-bt-per] from
 *    snippets/price.liquid), and redraw from the re-rendered page when the variant changes (same
 *    event the theme's price block uses). Since 2026-10-28 the cards also carry the pack
 *    ("Antal", e.g. 6 / 8 / 12 / 16 stole): a card for another pack presses the theme's own
 *    hidden "Antal" button, and the redraw after that variant change checks the card again.
 *    The theme re-renders the price and the sticky bar on its own; a MutationObserver on the
 *    section puts the chosen card's price back after each re-render.
 * 2. The reserve-set offer in the cart (snippets/cart-products.liquid): one tap adds the
 *    matching "Reservesæt – 8 stk." variant and lets the cart drawer / cart page re-render.
 *    Any failure falls back to the form's own POST to /cart/add.
 * 3. The leg-shape switch (blocks/bt-leg-switch.liquid): its link to the other product keeps the
 *    chosen pack and colour, never the size.
 *
 * Loaded as a module from those three places; a module with one URL runs once per page.
 * Nothing here touches checkout: "Til kassen" stays assets/udsolgt.js's job.
 */

const PRODUCT_SELECT = 'shopify:product:select';

/** @type {Record<string, number>} chosen sets per block id, kept across redraws */
const chosenSets = {};

/** @param {HTMLInputElement} radio */
function setsOf(radio) {
  return Number(radio.dataset.sets || radio.value) === 2 ? 2 : 1;
}

class BtSetCards extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.shopify-section') || document;
    const id = this.dataset.blockId || '';
    if (!(id in chosenSets)) chosenSets[id] = Number(this.dataset.defaultSets) === 2 ? 2 : 1;

    this.onChange = (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
      chosenSets[id] = setsOf(input);
      if (input.dataset.current === 'false') {
        /* A card for another pack: show its price now, then change the variant; the redraw follows. */
        this.applyPrices();
        this.pickPack(input.dataset.pack || '');
        return;
      }
      this.apply();
    };

    this.onProductSelect = (event) => {
      const origin = event.target;
      if (origin instanceof Element && origin.closest('product-card')) return;
      if (!event.promise || typeof event.promise.then !== 'function') return;
      event.promise
        .then((result) => {
          const html = result && result.detail && result.detail.html;
          if (!html || typeof html.querySelector !== 'function') return;
          const fresh = html.querySelector(`bt-set-cards[data-block-id="${CSS.escape(id)}"]`);
          this.innerHTML = fresh ? fresh.innerHTML : '';
          this.apply();
          /* The product form updates its own quantity field after the same event: set it again once that has run. */
          setTimeout(() => this.apply(), 0);
        })
        .catch(() => {});
    };

    this.addEventListener('change', this.onChange);
    this.section.addEventListener(PRODUCT_SELECT, this.onProductSelect);

    /* The price block and the sticky bar redraw themselves on variant change: re-apply the chosen price after. */
    this.pricePending = false;
    this.observer = new MutationObserver(() => {
      if (this.pricePending) return;
      this.pricePending = true;
      setTimeout(() => {
        this.pricePending = false;
        this.applyPrices();
      }, 0);
    });
    if (this.section instanceof Element) {
      this.observer.observe(this.section, { childList: true, subtree: true, characterData: true });
    }
    this.apply();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
    if (this.section) this.section.removeEventListener(PRODUCT_SELECT, this.onProductSelect);
    if (this.observer) this.observer.disconnect();
  }

  apply() {
    const id = this.dataset.blockId || '';
    const radios = Array.from(this.querySelectorAll('input[type="radio"]')).filter(
      (radio) => radio instanceof HTMLInputElement
    );
    if (!radios.length) {
      this.removeAttribute('data-active');
      this.setQuantity(1);
      return;
    }
    this.setAttribute('data-active', '');
    /* Only the cards of the variant on the page can be checked; a pack without a 2-set card falls back to 1 set. */
    const current = radios.filter((radio) => radio.dataset.current !== 'false');
    /* One pack only (data-one-pack, e.g. Ella and Ida): a page opened on another pack moves to the cards' pack. */
    const fieldset = this.querySelector('.bt-sets');
    if (!current.length && fieldset instanceof HTMLElement && fieldset.hasAttribute('data-one-pack')) {
      this.pickPack(radios[0].dataset.pack || '');
    }
    if (!current.some((radio) => setsOf(radio) === chosenSets[id])) chosenSets[id] = 1;
    const sets = chosenSets[id] || 1;
    radios.forEach((radio) => {
      radio.checked = radio.dataset.current !== 'false' && setsOf(radio) === sets;
    });
    this.setQuantity(sets);
    this.applyPrices();
  }

  /** Writes the chosen card's total and per-chair price into the product page price and the sticky bar. */
  applyPrices() {
    if (!this.hasAttribute('data-active')) return;
    const checked = this.querySelector('input.bt-set__input:checked');
    if (!(checked instanceof HTMLInputElement)) return;
    const amount = checked.dataset.amount;
    const per = checked.dataset.per;
    if (amount) {
      this.section.querySelectorAll('[data-bt-amount]').forEach((element) => {
        if (element.closest('bt-set-cards, product-card')) return;
        const target = element.querySelector('bdi') || element;
        if (target.textContent !== amount) target.textContent = amount;
      });
    }
    if (per) {
      this.section.querySelectorAll('[data-bt-per]').forEach((element) => {
        if (element.closest('bt-set-cards, product-card')) return;
        if (element.textContent !== per) element.textContent = per;
      });
    }
    /* MÅNEDENS UDSALG: the crossed-out price shows only with a card that has one (1 sæt), never beside a 2-set total. */
    const compare = checked.dataset.compare || '';
    this.section.querySelectorAll('[data-bt-compare]').forEach((element) => {
      if (!(element instanceof HTMLElement) || element.closest('bt-set-cards, product-card')) return;
      const hide = !compare;
      if (element.hidden !== hide) element.hidden = hide;
      const target = element.querySelector('bdi');
      if (compare && target && target.textContent !== compare) target.textContent = compare;
    });
  }

  /** Presses the theme's own (hidden) button for this pack, which changes the variant. @param {string} value */
  pickPack(value) {
    const fieldset = this.querySelector('.bt-sets');
    const option = fieldset instanceof HTMLElement ? fieldset.dataset.packOption : '';
    if (!option || !value) return;
    const button = Array.from(this.section.querySelectorAll('input[type="radio"][data-option-name]')).find(
      (input) =>
        input instanceof HTMLInputElement &&
        input.dataset.optionName === option &&
        input.value === value &&
        !input.closest('bt-set-cards, product-card')
    );
    if (button instanceof HTMLInputElement && !button.checked) button.click();
  }

  /** @param {number} sets */
  setQuantity(sets) {
    const selector = this.section.querySelector('product-form-component quantity-selector-component');
    const input = this.section.querySelector('product-form-component input[name="quantity"]');
    if (!input) return;
    if (String(input.value) === String(sets)) return;
    try {
      if (selector && typeof selector.setValue === 'function') {
        selector.setValue(String(sets));
      } else {
        input.value = String(sets);
      }
      if (selector && typeof selector.onQuantityChange === 'function') selector.onQuantityChange();
      if (selector && typeof selector.updateButtonStates === 'function') selector.updateButtonStates();
    } catch (error) {
      input.value = String(sets);
    }
  }
}

if (!customElements.get('bt-set-cards')) customElements.define('bt-set-cards', BtSetCards);

/* ---- Reserve set in the cart ---- */

function cartSectionIds() {
  const ids = [];
  document.querySelectorAll('cart-items-component').forEach((element) => {
    if (element instanceof HTMLElement && element.dataset.sectionId) ids.push(element.dataset.sectionId);
  });
  return ids;
}

/** @param {HTMLFormElement} form */
async function addReserve(form) {
  const button = form.querySelector('button');
  if (button) {
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
  }
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const formData = new FormData(form);
  formData.delete('return_to');
  const sections = cartSectionIds();
  if (sections.length) formData.append('sections', sections.join(','));

  /* Before anything is added: a failure here falls back to the plain form post. */
  const { CartLinesUpdateEvent } = await import('@shopify/events');

  let added = null;
  try {
    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData,
    });
    added = await response.json();
    if (!response.ok || added.status) throw new Error(added.description || added.message || 'add failed');
  } catch (error) {
    if (button) {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
    throw error;
  }

  /* The line is in the cart now: from here a failure reloads instead of adding a second one. */
  try {
    const deferred = CartLinesUpdateEvent.createPromise();
    form.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: String(formData.get('id')), quantity: 1 }],
        promise: deferred.promise,
      })
    );
    const cartResponse = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
    const cart = await cartResponse.json();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'bt-reserve-offer',
        sourceId: 'bt-reserve-offer',
        itemCount: 1,
        sections: added.sections,
        didError: false,
      },
    });
  } catch (error) {
    window.location.reload();
  }
}

document.addEventListener('submit', (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.matches('form[data-bt-reserve-form]')) return;
  event.preventDefault();
  if (form.dataset.btBusy === 'true') return;
  form.dataset.btBusy = 'true';
  addReserve(form)
    .catch(() => {
      /* Nothing was added: fall back to the plain form post (Shopify adds the line, then opens /cart). */
      HTMLFormElement.prototype.submit.call(form);
    })
    .finally(() => {
      form.dataset.btBusy = 'false';
    });
});

/*
 * 3. Leg-shape switch (blocks/bt-leg-switch.liquid): the link to the other leg shape keeps the chosen
 *    pack and colour. data-bt-leg-map maps each variant here to its match on the other product (never
 *    the size); right before the link is followed, point it at the match for the variant in this
 *    section's product form. No match: the other product's own default.
 */
/** @type {WeakMap<HTMLAnchorElement, Record<string, string>>} */
const legMaps = new WeakMap();

/** @param {HTMLAnchorElement} link */
function pointLegLink(link) {
  let map = legMaps.get(link);
  if (!map) {
    try {
      map = JSON.parse(link.dataset.btLegMap || '{}') || {};
    } catch (error) {
      map = {};
    }
    legMaps.set(link, map);
  }
  const section = link.closest('.shopify-section') || document;
  const input = section.querySelector('form[data-type="add-to-cart-form"] input[name="id"]');
  const base = link.dataset.btLegBase;
  if (!base || !(input instanceof HTMLInputElement)) return;
  const match = map[input.value];
  link.href = match ? `${base}?variant=${encodeURIComponent(match)}` : base;
}

for (const type of ['pointerdown', 'focusin', 'click']) {
  document.addEventListener(
    type,
    (event) => {
      const link = event.target instanceof Element ? event.target.closest('a[data-bt-leg-map]') : null;
      if (link instanceof HTMLAnchorElement) pointLegLink(link);
    },
    true
  );
}
